import { TOURS_SITE } from "../../mock";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ArrowRight, Repeat, Clock, Plane, MapPin, Calendar as CalIcon, Users, Luggage, Minus, Plus, Search, Compass, ExternalLink } from "lucide-react";
import LocationInput from "./LocationInput";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const Label = ({ children }) => (
  <label className="block text-[11.5px] sm:text-[12px] tracking-[0.08em] uppercase font-medium text-[#3E7F8E] mb-1 sm:mb-2">{children}</label>
);

const boxCls = "flex items-center gap-2 sm:gap-2.5 h-[44px] sm:h-[46px] rounded-xl border border-[#183C35]/15 bg-white px-3 sm:px-3.5 w-full";

export const DateField = ({ value, onChange, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className={`${boxCls} hover:border-[#183C35]/40 transition-colors text-left`} data-testid={testid}>
        <CalIcon className="w-4 h-4 text-[#3E7F8E] shrink-0" />
        <span className={`text-[14.5px] truncate ${value ? "text-[#202522]" : "text-[#202522]/45"}`}>{value ? format(value, "dd MMM yy") : "Select"}</span>
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0 rounded-xl" align="start">
      <Calendar mode="single" selected={value} onSelect={onChange} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
    </PopoverContent>
  </Popover>
);

export const TimeField = ({ value, onChange, testid }) => (
  <div className={`${boxCls} focus-within:border-[#183C35]`}>
    <input type="time" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-transparent outline-none text-[16px] sm:text-[14.5px] text-[#202522]" />
  </div>
);

const Counter = ({ icon: I, value, set, min = 1, max = 8, testid }) => (
  <div className={`${boxCls} justify-between gap-1 sm:gap-1 px-2 sm:px-2`}>
    <I className="w-4 h-4 text-[#3E7F8E] shrink-0" />
    <button type="button" onClick={() => set(Math.max(min, value - 1))} data-testid={`${testid}-minus`}
      className="w-9 h-9 sm:w-7 sm:h-7 shrink-0 rounded-full bg-[#F7F3EA] text-[#183C35] flex items-center justify-center hover:bg-[#EDE4D2] transition-colors"><Minus className="w-3.5 h-3.5" /></button>
    <span className="text-[15px] font-medium w-5 text-center" data-testid={`${testid}-value`}>{value}</span>
    <button type="button" onClick={() => set(Math.min(max, value + 1))} data-testid={`${testid}-plus`}
      className="w-9 h-9 sm:w-7 sm:h-7 shrink-0 rounded-full bg-[#F7F3EA] text-[#183C35] flex items-center justify-center hover:bg-[#EDE4D2] transition-colors"><Plus className="w-3.5 h-3.5" /></button>
  </div>
);

const TABS = [["oneway", "One Way", ArrowRight], ["round", "Round Trip", Repeat], ["hourly", "By the Hour", Clock]];

const BookingWidget = ({ preset = {}, className = "" }) => {
  const navigate = useNavigate();
  const [type, setType] = useState(preset.type || "oneway");
  const [pickup, setPickup] = useState(preset.pickup || "");
  const [dropoff, setDropoff] = useState(preset.dropoff || "");
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [rdate, setRdate] = useState();
  const [rtime, setRtime] = useState("");
  const [hours, setHours] = useState("3");
  const [pax, setPax] = useState(Number(preset.pax) || 2);
  const [bags, setBags] = useState(preset.bags !== undefined && preset.bags !== null && preset.bags !== "" ? Number(preset.bags) : 2);
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!pickup) return setError("Please choose a pickup location.");
    if (type !== "hourly" && !dropoff) return setError("Please choose a drop-off location.");
    if (!date) return setError("Please choose a pickup date.");
    if (!time) return setError("Please choose a pickup time.");
    if (type === "round" && (!rdate || !rtime)) return setError("Please choose a return date and time.");
    setError("");
    const p = new URLSearchParams({ type, pickup, date: format(date, "yyyy-MM-dd"), time, pax, bags });
    if (type !== "hourly") p.set("dropoff", dropoff); else p.set("hours", hours);
    if (type === "round") { p.set("rdate", format(rdate, "yyyy-MM-dd")); p.set("rtime", rtime); }
    navigate(`/book?${p.toString()}`);
  };

  return (
    <form onSubmit={submit} className={`bg-white rounded-2xl shadow-[0_30px_60px_-30px_rgba(0,0,0,.45)] p-4 sm:p-6 ${className}`} data-testid="booking-widget">
      <div className="grid grid-cols-2 min-[440px]:grid-cols-4 sm:flex sm:flex-wrap gap-1.5 sm:gap-2">
        {TABS.map(([k, l, I]) => (
          <button type="button" key={k} onClick={() => { setType(k); setError(""); }} data-testid={`trip-type-${k}`}
            className={`flex items-center justify-center gap-1.5 sm:gap-2 rounded-full px-2 sm:px-4 py-2.5 sm:py-2 text-[13px] sm:text-[14.5px] whitespace-nowrap transition-colors duration-200 ${type === k ? "bg-[#183C35] text-[#F7F3EA]" : "bg-[#F7F3EA] text-[#202522]/70 hover:text-[#183C35] hover:bg-[#EFE7D6]"}`}>
            <I className="w-3.5 h-3.5 hidden min-[400px]:block" />{l}
          </button>
        ))}
        <a href={TOURS_SITE} target="_blank" rel="noopener" data-testid="trip-type-tours"
          className="flex items-center justify-center gap-1.5 sm:gap-2 rounded-full px-2 sm:px-4 py-2.5 sm:py-2 text-[13px] sm:text-[14.5px] whitespace-nowrap transition-colors duration-200 bg-[#F7F3EA] text-[#202522]/70 hover:text-[#183C35] hover:bg-[#EFE7D6]">
          <Compass className="w-3.5 h-3.5 hidden min-[400px]:block" />Tours<ExternalLink className="w-3 h-3 opacity-60" />
        </a>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-5">
        <LocationInput label="Pickup location" value={pickup} onChange={setPickup} placeholder="e.g. Corfu Airport" icon={Plane} testid="pickup-input" />
        {type === "hourly" ? (
          <div>
            <Label>Duration</Label>
            <Select value={hours} onValueChange={setHours}>
              <SelectTrigger className="h-[44px] sm:h-[46px] rounded-xl border-[#183C35]/15 text-[15px]" data-testid="hours-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[3, 4, 5, 6, 8, 10, 12].map((h) => <SelectItem key={h} value={String(h)}>{h} hours</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <LocationInput label="Drop-off location" value={dropoff} onChange={setDropoff} placeholder="e.g. Corfu Palace Hotel" icon={MapPin} testid="dropoff-input" />
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-3 sm:gap-4 mt-3 sm:mt-4">
        <div><Label>Date</Label><DateField value={date} onChange={setDate} testid="date-input" /></div>
        <div><Label>Time</Label><TimeField value={time} onChange={setTime} testid="time-input" /></div>
        <div><Label>Passengers</Label><Counter icon={Users} value={pax} set={setPax} max={8} testid="pax" /></div>
        <div><Label>Luggage</Label><Counter icon={Luggage} value={bags} set={setBags} min={0} max={10} testid="bags" /></div>
      </div>
      {type === "round" && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4 fade-up" style={{ animationDuration: ".3s" }}>
          <div><Label>Return date</Label><DateField value={rdate} onChange={setRdate} testid="return-date-input" /></div>
          <div><Label>Return time</Label><TimeField value={rtime} onChange={setRtime} testid="return-time-input" /></div>
        </div>
      )}
      {error && <p className="mt-4 text-[14.5px] text-[#B42318]" data-testid="booking-error">{error}</p>}
      <button type="submit" data-testid="see-prices-btn"
        className="mt-4 sm:mt-5 w-full h-[48px] sm:h-[52px] rounded-xl bg-[#183C35] text-[#F7F3EA] font-semibold text-[15px] flex items-center justify-center gap-2 hover:bg-[#21524A] transition-colors duration-200">
        <Search className="w-4 h-4" /> See prices &amp; vehicles
      </button>
    </form>
  );
};

export default BookingWidget;
